import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarIcon, Loader2, Gift, ArrowLeftRight } from "lucide-react";
import { adminApi, errMsg } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

const STATE_STYLE = { active: "bg-emerald-50 text-emerald-800", upcoming: "bg-sky-50 text-sky-800", expired: "bg-zinc-100 text-zinc-700", inactive: "bg-zinc-100 text-zinc-700" };

function DatePick({ value, onChange, testId }) {
  const d = value ? new Date(value) : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start gap-2" data-testid={testId}><CalendarIcon className="h-4 w-4" />{d ? fmtDate(d.toISOString(), false) : "Pick a date"}</Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={d} onSelect={(x) => x && onChange(new Date(Date.UTC(x.getFullYear(), x.getMonth(), x.getDate())).toISOString())} />
      </PopoverContent>
    </Popover>
  );
}

function BonusCard({ cfg }) {
  const qc = useQueryClient();
  const [f, setF] = useState(cfg);
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(cfg), [cfg]);
  const cur = cfg.currency;
  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put(`/admin/bonus-config/${cur}`, {
        rate_pct: parseFloat(f.rate_pct), max_eligible: parseFloat(f.max_eligible), is_active: f.is_active, time_bound: f.time_bound,
        campaign_start: f.time_bound ? f.campaign_start : null, campaign_end: f.time_bound ? f.campaign_end : null,
      });
      toast.success(`${cur} bonus updated`);
      qc.invalidateQueries({ queryKey: ["admin-bonus"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const set7 = () => {
    const s = f.campaign_start ? new Date(f.campaign_start) : new Date(new Date().setUTCHours(0, 0, 0, 0));
    setF({ ...f, campaign_start: s.toISOString(), campaign_end: new Date(s.getTime() + 7 * 86400000).toISOString() });
  };
  return (
    <Card data-testid={`bonus-card-${cur.toLowerCase()}`}>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base"><Gift className="h-4 w-4 text-amber-700" /> {cur} deposit bonus</CardTitle>
        <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", STATE_STYLE[cfg.state])} data-testid={`bonus-state-${cur.toLowerCase()}`}>{cfg.state}</span>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Bonus rate (%)</Label><Input inputMode="decimal" value={f.rate_pct} onChange={(e) => setF({ ...f, rate_pct: e.target.value })} className="num" data-testid={`bonus-rate-input-${cur.toLowerCase()}`} /></div>
          <div className="space-y-1.5"><Label>Max eligible per deposit</Label><Input inputMode="decimal" value={f.max_eligible} onChange={(e) => setF({ ...f, max_eligible: e.target.value })} className="num" data-testid={`bonus-max-input-${cur.toLowerCase()}`} /></div>
        </div>
        <label className="flex items-center justify-between rounded-lg border p-3"><span className="text-sm font-medium">Bonus enabled</span><Switch checked={f.is_active} onCheckedChange={(v) => setF({ ...f, is_active: v })} data-testid={`bonus-active-switch-${cur.toLowerCase()}`} /></label>
        <label className="flex items-center justify-between rounded-lg border p-3"><span><span className="block text-sm font-medium">Time-limited campaign</span><span className="text-xs text-muted-foreground">Bonus applies only between start and end</span></span><Switch checked={f.time_bound} onCheckedChange={(v) => setF({ ...f, time_bound: v })} data-testid={`bonus-timebound-switch-${cur.toLowerCase()}`} /></label>
        {f.time_bound && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Starts</Label><DatePick value={f.campaign_start} onChange={(v) => setF({ ...f, campaign_start: v })} testId={`bonus-start-date-${cur.toLowerCase()}`} /></div>
            <div className="space-y-1.5"><Label>Ends</Label><DatePick value={f.campaign_end} onChange={(v) => setF({ ...f, campaign_end: v })} testId={`bonus-end-date-${cur.toLowerCase()}`} /></div>
            <Button variant="ghost" size="sm" className="col-span-2 justify-start text-primary" onClick={set7} data-testid={`bonus-set-7days-${cur.toLowerCase()}`}>Set 7-day window from start date</Button>
          </div>
        )}
        <div className="rounded-lg bg-secondary p-3 text-xs text-muted-foreground">
          Example: {fmtMoney(parseFloat(f.max_eligible) || 0, cur)} deposit → <span className="num font-medium text-foreground">{fmtMoney(((parseFloat(f.max_eligible) || 0) * (parseFloat(f.rate_pct) || 0)) / 100, cur)}</span> bonus. Paid so far: <span className="num font-medium text-foreground">{fmtMoney(cfg.total_bonus_paid, cur)}</span> across {cfg.bonus_count} deposits.
        </div>
        <Button onClick={save} disabled={busy} className="w-full" data-testid={`bonus-save-button-${cur.toLowerCase()}`}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save {cur} bonus</Button>
      </CardContent>
    </Card>
  );
}

function RatesCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-rates"], queryFn: () => adminApi.get("/admin/rates").then((r) => r.data) });
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data?.rates) setF({ buy_rate: String(data.rates.buy_rate), sell_rate: String(data.rates.sell_rate), fee_pct: String(data.rates.fee_pct) }); }, [data]);
  if (!f) return <Skeleton className="h-80" />;
  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put("/admin/rates", { buy_rate: parseFloat(f.buy_rate), sell_rate: parseFloat(f.sell_rate), fee_pct: parseFloat(f.fee_pct) || 0 });
      toast.success("Exchange rates updated");
      qc.invalidateQueries({ queryKey: ["admin-rates"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="lg:col-span-2">
      <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><ArrowLeftRight className="h-4 w-4 text-primary" /> Exchange rates (INR per USDT)</CardTitle></CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Buy rate</Label><Input value={f.buy_rate} onChange={(e) => setF({ ...f, buy_rate: e.target.value })} className="num" data-testid="rates-buy-input" /></div>
            <div className="space-y-1.5"><Label>Sell rate</Label><Input value={f.sell_rate} onChange={(e) => setF({ ...f, sell_rate: e.target.value })} className="num" data-testid="rates-sell-input" /></div>
            <div className="space-y-1.5"><Label>Fee %</Label><Input value={f.fee_pct} onChange={(e) => setF({ ...f, fee_pct: e.target.value })} className="num" data-testid="rates-fee-input" /></div>
          </div>
          <p className="text-xs text-muted-foreground">Buy rate: users pay this many INR for 1 USDT. Sell rate: users receive this many INR per USDT. Last updated {fmtDate(data.rates.updated_at)} by {data.rates.updated_by}.</p>
          <Button onClick={save} disabled={busy} data-testid="rates-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save rates</Button>
        </div>
        <div className="max-h-64 overflow-auto rounded-lg border scrollbar-thin">
          <Table>
            <TableHeader><TableRow><TableHead>When</TableHead><TableHead className="text-right">Buy</TableHead><TableHead className="text-right">Sell</TableHead><TableHead className="text-right">Fee</TableHead><TableHead>By</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.history.map((h) => (
                <TableRow key={h.id}><TableCell className="text-xs">{fmtDate(h.created_at)}</TableCell><TableCell className="num text-right text-xs">{h.buy_rate}</TableCell><TableCell className="num text-right text-xs">{h.sell_rate}</TableCell><TableCell className="num text-right text-xs">{h.fee_pct}%</TableCell><TableCell className="text-xs">{h.by}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminBonuses() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-bonus"], queryFn: () => adminApi.get("/admin/bonus-config").then((r) => r.data) });
  return (
    <div>
      <PageHeader title="Bonuses & rates" subtitle="Bonus rules are snapshotted when a user submits and applied once on approval." />
      <div className="grid gap-4 lg:grid-cols-2">
        {isLoading ? [0, 1].map((i) => <Skeleton key={i} className="h-96" />) : data.map((b) => <BonusCard key={b.currency} cfg={b} />)}
        <RatesCard />
      </div>
    </div>
  );
}
